import { X, Paperclip, Loader2 } from 'lucide-react';
import { usePancakeStore } from '../../store/pancake.store';
import type { PancakeConversation } from '../../types/pancake';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  conversation: PancakeConversation;
  isDark: boolean;
}

export default function ConversationDetail({ isOpen, onClose, conversation, isDark }: Props) {
  const { conversationMessages, isLoading } = usePancakeStore();

  if (!isOpen) return null;

  const isPageMessage = (fromId: string) => {
    // Messages from the page have from.id matching the page id
    return fromId === conversation._pageId;
  };

  const formatTime = (ts: string) => {
    try {
      const d = new Date(ts);
      return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return ts;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className={`relative w-full max-w-2xl max-h-[80vh] flex flex-col rounded-xl border shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <div>
            <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {conversation.from?.name || 'Hội thoại'}
            </h3>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {conversation._pageName} &middot; {conversation.type}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
            </div>
          )}

          {!isLoading && conversationMessages.length === 0 && (
            <p className={`text-center py-12 text-sm ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              Không có tin nhắn
            </p>
          )}

          {!isLoading && conversationMessages.map((msg) => {
            const fromPage = isPageMessage(msg.from?.id);
            return (
              <div
                key={msg.id}
                className={`flex ${fromPage ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${
                  fromPage
                    ? isDark ? 'bg-cyan-500/20 text-cyan-100' : 'bg-cyan-50 text-cyan-900'
                    : isDark ? 'bg-slate-800 text-slate-200' : 'bg-gray-100 text-gray-800'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${
                    fromPage
                      ? isDark ? 'text-cyan-400' : 'text-cyan-600'
                      : isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    {msg.from?.name || 'N/A'}
                  </p>
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {msg.message || msg.original_message || '(trống)'}
                  </p>
                  <div className={`flex items-center gap-2 mt-1.5 text-[11px] ${
                    fromPage
                      ? isDark ? 'text-cyan-500/60' : 'text-cyan-500'
                      : isDark ? 'text-slate-500' : 'text-gray-400'
                  }`}>
                    <span>{formatTime(msg.inserted_at)}</span>
                    {msg.attachments && msg.attachments.length > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <Paperclip className="w-3 h-3" />
                        {msg.attachments.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
